import { m } from "framer-motion";
import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { cn } from "../cn";
import { springSoft } from "../motion/variants";
import { useMotionTransition } from "../motion/useMotionTransition";
import { useSlidingIndicator } from "./useSlidingIndicator";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional secondary line rendered under the label. */
  hint?: ReactNode;
  disabled?: boolean;
}

interface SegmentedProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Accessible label for the radiogroup. */
  "aria-label": string;
  size?: "sm" | "md";
  /** Stretch to fill the container width with equal-width segments. */
  fluid?: boolean;
  /**
   * Corner treatment. `"pill"` (default) is fully round — right for toolbar
   * toggles. `"rounded"` uses soft concentric corners that sit cohesively
   * among `rounded-xl` form controls (e.g. inside a form column).
   */
  shape?: "pill" | "rounded";
  className?: string;
}

/**
 * Compact, accessible segmented control with a measured sliding pill.
 * Behaves as a radio group: arrow keys move between segments.
 *
 * This is a presentational atom: it owns selection/keyboard behaviour only.
 * Consumers pass `value`/`onValueChange`, mirroring the pattern used by other
 * primitives such as `FilterToggle`.
 */
export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  size = "md",
  fluid = false,
  shape = "pill",
  className,
  "aria-label": ariaLabel,
}: SegmentedProps<T>) {
  // The selected pill is a single measured element that slides/resizes to the
  // active segment. It is deliberately not a shared-layout `layoutId`: such an
  // id inside an `AnimatePresence mode="wait"` exiting subtree never resolves
  // its projection, hanging the exit so the incoming screen never mounts and
  // only the grey shell canvas remains.
  const { containerRef, indicator } =
    useSlidingIndicator<HTMLDivElement>(value);
  const indicatorTransition = useMotionTransition(springSoft);

  // Concentric corners: the container insets its segments by `p-1` (4px), so
  // the inner pill/segment radius is one step tighter than the shell to keep
  // the nested curves visually parallel.
  const shellRadius = shape === "rounded" ? "rounded-xl" : "rounded-full";
  const innerRadius = shape === "rounded" ? "rounded-lg" : "rounded-full";

  // Roving-tabindex requires moving DOM focus along with the selection:
  // keydown fires on the currently-focused button, and after
  // onValueChange re-renders, that button becomes tabIndex={-1}. Without
  // an explicit .focus() call, browser focus is silently stranded on a
  // now-unreachable element instead of following the visible selection.
  const optionRefs = useRef(new Map<T, HTMLButtonElement | null>());

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const enabled = options.filter((option) => !option.disabled);
    const currentIndex = enabled.findIndex((option) => option.value === value);
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = -1;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % enabled.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = enabled.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = enabled[nextIndex].value;
    onValueChange(next);
    optionRefs.current.get(next)?.focus();
  }

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        "relative items-stretch border border-border/70 bg-muted/60 p-1",
        shellRadius,
        // Fluid segments are already separated by their own inline padding and
        // the sliding pill; a gap only steals width the labels need.
        fluid ? "flex w-full gap-0" : "inline-flex gap-1",
        className,
      )}
    >
      {indicator && (
        <m.span
          aria-hidden
          className={cn(
            "pointer-events-none absolute left-0 top-0 bg-surface shadow-sm ring-1 ring-border/60",
            innerRadius,
          )}
          initial={false}
          animate={{
            x: indicator.left,
            y: indicator.top,
            width: indicator.width,
            height: indicator.height,
          }}
          transition={indicatorTransition}
        />
      )}
      {options.map((option) => {
        const selected = option.value === value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: a native <input type="radio"> can't host the rendered label/hint content or the sliding-pill visual treatment; role="radio" on a <button> is the standard WAI-ARIA pattern for a custom-styled radio group (e.g. Radix's RadioGroup.Item).
          <button
            key={option.value}
            ref={(node) => {
              optionRefs.current.set(option.value, node);
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            data-active={selected ? "true" : undefined}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "relative inline-flex flex-col items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-40",
              innerRadius,
              size === "sm"
                ? "h-8 px-3.5 text-[0.8125rem]"
                : "h-10 px-4 text-sm",
              // Fluid segments share the shell's width. `basis-auto grow`
              // sizes them by their content rather than equally (plain
              // `flex-1` forces equal widths, truncating the longer labels
              // while short ones keep slack), `min-w-0` lets them shrink below
              // the label's intrinsic width — flex items default to
              // `min-width:auto`, which is what pushed "Gearchiveerd" out past
              // the rounded edge — and the tighter inline padding buys back
              // real label room.
              fluid && "min-w-0 shrink basis-auto grow px-1.5",
              selected
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "relative z-10 leading-tight",
                // Truncate rather than wrap: the segment height is fixed, so a
                // second line would sit outside the pill.
                fluid && "max-w-full truncate",
              )}
            >
              {option.label}
            </span>
            {option.hint && (
              <span
                className={cn(
                  "relative z-10 text-[0.625rem] font-normal leading-tight",
                  selected
                    ? "text-muted-foreground"
                    : "text-muted-foreground/70",
                )}
              >
                {option.hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
